<template>
    <div class="datatable">
        <spinner line-fg-color="#F02629" v-if="filtersLoading" class="w-full mt-4 flex-col"></spinner>
        <div class="flex justify-start mt-2 flex-wrap" v-if="!filtersLoading">
            <div v-for="(filter, index) in orderedFilters" v-bind:key="index" :class="['relative pr-4 mb-3 '+filter.width]">
                <label class="block w-full text-xs pb-1 text-charcoal-900">
                    {{ filter.label }}
                </label>
                <!-- Text Input -->
                <input v-if="filter.type === 'text'"
                    :id="filter.name"
                    type="text"
                    class="font-light border border-gray-400 py-2 px-3 w-full text-sm focus:outline-none disabled:bg-white disabled:opacity-50"
                    :placeholder="filter.placeholder"
                    v-model="filter.value"
                    v-on:keydown="updateFilterStore(filter)"
                    :disabled="requesting"/>
                <the-mask v-if="filter.type === 'text-mask'"
                    :id="filter.name"
                    type="text"
                    class="font-light border border-gray-400 py-2 px-3 w-full text-sm focus:outline-none disabled:bg-white disabled:opacity-50"
                    :placeholder="filter.placeholder"
                    v-model="filter.value"
                    v-on:input="updateFilterStore(filter)"
                    :disabled="requesting"
                    :mask="filter.mask"
                    :masked="true"/>
                <font-awesome-icon v-if="filter.type === 'text' || filter.type === 'text-mask' && filter.value !== ''"
                    class="right-0 absolute mt-3 mr-7 text-gray-500 hover:text-gray-700 cursor-pointer"
                    :icon="['fal', 'times']"
                    @click="clearFilter(filter)" />
                <!-- Select Input -->
                <input-select v-if="filter.type === 'select'"
                    :id="filter.name"
                    v-model="filter.value"
                    :options="filter.options"
                    :initial-value="filter.value"
                    @input="function(val){ selectChange(filter, val); }"
                    :placeholder="filter.placeholder"
                    :disabled="requesting" />
                <!-- Date Range -->
                <date-range-picker
                    ref="picker"
                    v-if="filter.type === 'dateRange'"
                    :opens="filter.position ? filter.position : 'left'"
                    :locale-data="{ firstDay: 1, format: 'DD-MM-YYYY HH:mm:ss' }"
                    :singleDatePicker="false"
                    :showWeekNumbers="true"
                    :autoApply="true"
                    v-model="filter.value"
                    @update="function(val){ dateRangeChange(filter, val); }"
                    :ranges="ranges"
                >
                    <template v-slot:input="picker" style="min-width: 350px;">
                        {{ picker.startDate | moment("DD/MM/YYYY") }} - {{ picker.endDate | moment("DD/MM/YYYY") }}
                        <font-awesome-icon v-if="filter.type === 'dateRange' && filter.value !== '' && filter.value !== {}"
                            class="right-0 absolute mt-1 mr-3 text-gray-500 text-gray-700 cursor-pointer"
                            :icon="['fas', 'times']"
                            @click="clearFilter(filter)" />
                    </template>
                </date-range-picker>
            </div>
        </div>
        <div class="flex flex-col" v-if="!filtersLoading">
            <div class="-my-2 py-2 overflow-x-auto relative">
                <div class="align-middle inline-block min-w-full  border-gray-200 bg-white relative">
                    <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="requesting"><spinner line-fg-color="#F02629" size="35" :line-size="3"></spinner></div>
                    <table class="min-w-full mb-20" :class="[{'opacity-15': requesting}, tableClass]" id="pro_table">
                        <thead>
                            <tr>
                                <th v-for="(field, index) in fields"
                                    v-bind:key="index"
                                    class="table-head-cell"
                                    :class="[{ 'cursor-pointer': field.sortCol || field.tooltip, asc: filterClass(field, 'asc'), desc: filterClass(field, 'desc'), 'text-center': field.align === 'center', 'text-right': field.align === 'right'}, field.width ]"
                                    v-on:click="setFilter(field)">
                                    <span v-tooltip="field.tooltip">{{ field.name }}</span>
                                    <font-awesome-icon v-if="field.sortCol && filterClass(field, 'asc')"
                                        class="text-xl absolute ml-2"
                                        style="top: 14px;"
                                        :icon="['fas', 'sort-up']" />
                                    <font-awesome-icon v-if="field.sortCol && filterClass(field, 'desc')"
                                        class="text-xl absolute ml-2"
                                        style="top: 5px;"
                                        :icon="['fas', 'sort-down']" />
                                </th>
                                <th v-if="actions"
                                    class="table-head-cell"
                                    :class="actions.class">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, rowIndex) in items" v-bind:key="item.id ? item.id : rowIndex">
                                <cell v-for="(field, fieldIndex) in fields"
                                    v-bind:key="fieldIndex"
                                    :index="rowIndex"
                                    :field="field"
                                    :fieldIndex="fieldIndex"
                                    :item="item"
                                    :sortColumn="sortColumn"
                                    :hideOrdering="hideOrdering"
                                    @move="moveHandler"
                                    @toggle="toggleHandler"
                                    :value="getValueByProp(item, field.column, field.columns)"
                                    :actions="actions" />
                                <actions v-if="actions && actions.options.length > 0"
                                    :row="rowIndex"
                                    :item="item"
                                    :actions="actions"
                                    :resource="resource"
                                    :prefix="prefix"
                                    @action="actionHandler"
                                    @delete="deleteModal"
                                    :permissions="permissions"
                                    :redirect="redirect" />
                            </tr>
                        </tbody>
                        <tfoot v-if="sum">
                            <tr>
                                <column-sum-cell v-for="(field, fieldIndex) in fields" v-bind:key="fieldIndex" :sum="sum" :field="field" :items="items" />
                                <td>&nbsp;</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <pagination v-if="!loading && pagination && items.length > 0"
                    :max-visible-buttons="10"
                    :per-page="perPage"
                    :total="total"
                    :current-page="page"
                    @paginate="paginate"/>
                <transition name="fade">
                    <delete-confirmation-modal v-if="showDeleteModal" @close="closeDeleteModal" @delete="deleteItem" ref="datatableDeleteModal">
                        <template slot="content" v-if="selectedItem.deleteLabel">Are you sure you want to delete <strong>{{ selectedItem.deleteLabel }}</strong>?</template>
                    </delete-confirmation-modal>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from "axios";
    import _ from 'lodash';
    import Actions from './Actions';
    import Cell from './Cell';
    import ColumnSumCell from './ColumnSumCell';
    import Spinner from 'vue-simple-spinner';
    import Pagination from './Pagination';
    import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";
    import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
    import DateRangePicker from 'vue2-daterange-picker';

    export default {
        name: "ProTable",
        components: {
            Spinner,
            Pagination,
            Actions,
            Cell,
            ColumnSumCell,
            DeleteConfirmationModal,
            DateRangePicker
        },
        props: {
            permissions: { default: false }, // Client side permission checks
            fields: { default: null }, // Columns for table
            resource: { default: null }, // API endpoint resource
            prefix: { default: null }, // For slug before resource for actions ONLY
            limit: { default: null }, // Limit the results from API endpoint
            sort: { default: null }, // Column to sort
            direction: { default: null }, // Column sort direction
            filters: { default: null, }, // Filters for table
            deleteLabel: { default: null }, // Text to be displayed on Delete modal
            filtersLoading: { default: false }, // Toggle for displaying filters
            tableClass: { default: 'text-xs' }, // Class applied to table
            query: { type: Array, default: null }, // For passing query params straight through to API
            pagination: { default: true }, // Enable/Show Pagination
            sum: { default: null }, // Footer with column totals
            reference: { default: 'pro-table' }, // Reference for DataTable instance
            staticData: { default: null }, // Data passed in directly instead of using an API call
            with: { default: null }, // With Relationships
            redirect: { default: false }, // Set to true to take the user back to the page that begun the route change
            actions: {
                default () {
                    return {
                        display: true,
                        class: [],
                        options: [
                            { label: 'View', link: 'view', icon: 'eye',  type: 'router-link', tooltip: 'View', permissions: this.resource+'.show' },
                            { label: 'Edit', link: 'edit', icon: 'edit', type: 'router-link', tooltip: 'Edit', permissions: this.resource+'.edit' },
                            { label: 'Delete', method: 'delete', icon: 'trash-alt', type: 'method', tooltip: 'Delete', permissions: this.resource+'.destroy' },
                        ]
                    }
                }
            },
        },
        data: function(){
            return {
                items: [],
                total: 0,
                page: 1,
                sortColumn: this.sort ? this.sort : this.fields[0]['sortCol'],
                sortDir: this.direction ? this.direction : 'asc',
                perPage: this.limit ? this.limit : 25,
                loading: true,
                filterParams: this.$store.getters.filters[this.resource.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); })],
                selectedItem: null,
                selectedItemIndex: null,
                showDeleteModal: false,
                hideOrdering: false,
                requesting: false,
                ranges: {}
            }
        },
        beforeMount() {
            let today = new Date()
            today.setHours(0, 0, 0, 0)

            let yesterday = new Date()
            yesterday.setDate(today.getDate() - 1)
            yesterday.setHours(0, 0, 0, 0);

            let weekAgo = new Date();
            weekAgo.setDate(today.getDate() - 7)
            weekAgo.setHours(0, 0, 0, 0);

            this.ranges = {
                'Today': [today, today],
                'Yesterday': [yesterday, yesterday],
                'This Week': this.startAndEndOfWeek(today),
                'Last Week': this.startAndEndOfWeek(weekAgo),
                'This month': [new Date(today.getFullYear(), today.getMonth(), 1), new Date(today.getFullYear(), today.getMonth() + 1, 0)],
                'Last month': [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
                'This year': [new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31)],
            }
        },
        mounted() {
            if (!this.filtersLoading){
                this.getData();
            }
        },
        methods: {
            getData: function(){

                if (!this.requesting){
                    this.requesting = true;

                    let permissionGranted = false;

                    if (this.permissions){
                        if (this.$store.getters.permissions.indexOf(this.resource.replace("/", ".")+'.index') !== -1){
                            permissionGranted = true;
                        }
                    } else {
                        permissionGranted = true;
                    }

                    if (permissionGranted){
                        if (this.staticData){
                            this.items = this.staticData;
                            this.total = this.items.length;
                            this.requesting = false;
                            this.loading = false;
                            this.$Progress.finish();
                        } else {
                            this.$Progress.start();
                            this.loading = true;
                            this.$scrollTo('body', 500);

                            axios.get(process.env.VUE_APP_API_URL + '/'+this.resource, {
                                params: {
                                    sortColumn: this.sortColumn,
                                    sortDir: this.sortDir,
                                    page: this.page,
                                    limit: parseInt(this.perPage),
                                    response: 'datatable',
                                    filters: this.stateFilters,
                                    with: this.with,
                                    query: JSON.stringify(this.query)
                                }
                            }).then(response => {
                                this.items = response.data.data;
                                new Promise((resolve) => this.$emit('rowData', this.items, resolve)).then((data) => { this.items = data; });

                                if (response.data.meta){
                                    this.total = response.data.meta.total;
                                } else {
                                    this.total = response.data.total;
                                }

                                this.requesting = false;
                                this.loading = false;
                                this.$Progress.finish();
                            }).catch(() => {
                                this.requesting = false;
                                this.loading = false;
                                this.$Progress.finish();
                            });
                        }
                    } else {
                        this.requesting = false;
                    }
                }
            },
            actionHandler: function(data){
                data.ref = this.reference;
                this.$emit('action', data);
            },
            toggleHandler: function(data){
                axios.post(process.env.VUE_APP_API_URL + '/' + this.resource + '/' + data.item.id + '/toggle', { field: data.column }).then( response => {
                    if (response.data.success){
                        this.$notify({
                            group: 'main',
                            position: 'bottom right',
                            type: 'success',
                            title: 'Success',
                            text: response.data.message,
                            duration: 5000
                        });
                    }
                });
            },
            setFilter: function(field){
                let column;
                this.requesting = false;

                if (field.sortCol !== false && field.sortCol !== undefined){
                    if (field.sortCol){
                        column = field.sortCol;
                    } else {
                        // eslint-disable-next-line
                        column = field.column;
                    }

                    if (column !== this.sortColumn){
                        this.sortColumn = column;
                        this.sortDir = 'asc';
                    } else {
                        if (this.sortDir === 'asc'){
                            this.sortDir = 'desc';
                        } else {
                            this.sortDir = 'asc';
                        }
                    }

                    if (this.staticData){
                        this.$emit('sort', {column: this.sortColumn, direction: this.sortDir})
                    } else {
                        this.getData();
                    }

                }
            },
            clearFilter: function(filter){
                this.loading = true;
                this.hideOrdering = false;
                filter.value = '';
                this.filterParams[filter.name] = { value: '' };
                this.$store.commit('SET_FILTERS', { resource: this.resource.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); }), data: this.filterParams });
                this.page = 1;

                if (!this.filtersLoading){
                    this.getData();
                }
            },
            filterClass: function(field, direction){
                let column;
                if (field.sortCol){
                    column = field.sortCol;
                } else {
                    // eslint-disable-next-line
                    column = field.column;
                }

                return this.sortColumn === column && this.sortDir === direction;
            },
            selectChange: function(item, value){
                this.loading = true;

                if (value){
                    this.filterParams[item.name] = value;
                } else {
                    this.filterParams[item.name] = {};
                }

                this.$store.commit('SET_FILTERS', { resource: this.resource.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); }), data: this.filterParams });
                this.page = 1;

                if (!this.filtersLoading){
                    this.getData();
                }

            },
            dateRangeChange: function(item, value){
                this.loading = true;

                if (value){
                    this.filterParams[item.name] = value;
                } else {
                    this.filterParams[item.name] = {};
                }

                this.$store.commit('SET_FILTERS', { resource: this.resource.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); }), data: this.filterParams });
                this.page = 1;

                if (!this.filtersLoading){
                    this.getData();
                }

            },
            getValueByProp: function(item, prop, array){
                if (!prop && array){
                    return array.map(arrayProp => {
                        return _.get(item, arrayProp);
                    });
                } else {
                    return _.get(item, prop);
                }
            },
            moveHandler: function(data){
                let direction;
                let new_index;

                if (data.method === 'moveUp'){
                    new_index = data.index - 1;
                    direction = 'up';
                } else if (data.method === 'moveDown'){
                    new_index = data.index + 1;
                    direction = 'down';
                }

                while (data.index < 0) {
                    data.index += this.items.length;
                }
                while (new_index < 0) {
                    new_index += this.items.length;
                }
                if (new_index >= this.items.length) {
                    let k = new_index - this.items.length + 1;
                    while (k--) {
                        this.items.push(undefined);
                    }
                }
                this.items.splice(new_index, 0, this.items.splice(data.index, 1)[0]);

                // Send to Server
                window.axios.post(process.env.VUE_APP_API_URL + '/' + this.resource + '/' + data.item.id + '/move', {direction: direction, id: data.item.id }).then(response => {
                    if (response.data.success){
                        this.$notify({
                            group: 'main',
                            position: 'bottom right',
                            type: 'success',
                            title: 'Success',
                            text: response.data.message,
                            duration: 5000
                        });
                    }
                });
            },
            deleteModal: function(value){
                this.selectedItem = value.item;
                this.selectedItemIndex = value.index;

                if (this.deleteLabel){
                    const values = [];
                    const sp = this.deleteLabel.split(' ');

                    for (let i = 0, len = sp.length; i < len; i++) {
                        values.push(this.selectedItem[sp[i]])
                    }

                    this.selectedItem.deleteLabel = values.join(' ');
                }

                this.$store.commit('TOGGLE_MODAL', { open: true });
                this.showDeleteModal = true;
            },
            paginate: function(payload){
                this.$store.commit('SET_FILTERS', { resource: this.resource.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); }), data: this.filterParams });
                this.page = payload;

                if (!this.filtersLoading){
                    this.getData();
                }
            },
            closeDeleteModal: function(){
                if (this.showDeleteModal){
                    this.showDeleteModal = false;
                    this.selectedItem = null
                    document.body.className = '';
                    this.$store.commit('TOGGLE_MODAL', { open: false });
                }
            },
            deleteItem: function(){
                this.$Progress.start();
                this.$store.commit('TOGGLE_BACKDROP', {open: true});

                axios.delete(process.env.VUE_APP_API_URL + '/'+this.resource+'/'+this.selectedItem.id).then(response => {
                    let message;

                    if (response.data.success){
                        this.showDeleteModal = false;

                        document.body.className = '';
                        this.$store.commit('TOGGLE_MODAL', { open: false });
                        this.$store.commit('TOGGLE_BACKDROP', {open: false});

                        if (this.staticData && this.selectedItemIndex){
                            this.staticData.splice(this.selectedItemIndex, 1);
                        } else {
                            this.getData();
                        }

                        this.selectedItem = null;
                        this.selectedItemIndex = null;

                        if (response.data.message){
                            message = response.data.message;
                        } else {
                            message = 'Successfully Deleted Record!';
                        }

                        this.$notify({
                            group: 'main',
                            position: 'top center',
                            type: 'success',
                            title: 'Success',
                            text: message,
                            duration: 10000,
                            speed: 1000
                        });
                    } else {
                        this.showDeleteModal = false;
                        document.body.className = '';

                        this.$store.commit('TOGGLE_MODAL', { open: false });
                        this.$store.commit('TOGGLE_BACKDROP', {open: false});

                        this.$notify({
                            group: 'main',
                            position: 'bottom right',
                            type: 'error',
                            title: 'Error',
                            text: response.data.message,
                            duration: 5000
                        });
                    }

                    this.$Progress.finish();
                })
                .catch(() => {
                    this.$Progress.finish();
                });
            },
            startAndEndOfWeek: function(date) {

                // If no date object supplied, use current date
                // Copy date so don't modify supplied date
                const now = date ? new Date(date) : new Date();

                // set time to some convenient value
                now.setHours(0,0,0,0);

                // Get the previous Monday
                const monday = new Date(now);
                monday.setDate(monday.getDate() - monday.getDay() + 1);

                // Get next Sunday
                const sunday = new Date(now);
                sunday.setDate(sunday.getDate() - sunday.getDay() + 7);

                // Return array of date objects
                return [monday, sunday];
            },
            updateFilterStore: _.debounce(function(item, value){
                this.requesting = false;
                this.loading = true;
                this.hideOrdering = true;

                if (item.type === 'select'){
                    if (value){
                        this.filterParams[item.name] = value;
                    } else {
                        this.filterParams[item.name] = {};
                    }
                } else {
                    this.filterParams[item.name] = { value: item.value };
                }

                this.$store.commit('SET_FILTERS', { resource: this.resource.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); }), data: this.filterParams });
                this.page = 1;

                if (!this.filtersLoading){
                    this.getData();
                }
            }, 1000)
        },
        computed: {
            stateFilters () {
                let filters = this.$store.getters.filters;
                return filters[this.resource.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); })];
            },
            orderedFilters () {
                return _.orderBy(this.filters, ['ordering'], ['asc']);
            }
        },
        watch: {
            filtersLoading: function(newVal, oldVal){
                if (oldVal === true && newVal === false){
                    this.getData();
                }
            },
            staticData: function(newVal, oldVal){
                if (newVal.length !== oldVal.length){
                    this.items = this.staticData;
                }
            }
        },
    }
</script>

<style>
    .datatable .daterangepicker .calendars {
        display: flex;
    }
    .datatable .daterangepicker .ranges li {
        font-size: 11px;
    }
    @media screen and (min-width: 339px) {
        .datatable .daterangepicker.single.show-ranges.show-weeknumbers,
        .datatable .daterangepicker.single.show-ranges {
            min-width: 358px;
        }
    }
    .datatable .vue-daterange-picker {
        min-width: 100%!important;
    }
    .datatable .reportrange-text {
        border: 1px solid #dae1e7!important;
        color: #606f7b;
        font-size: 14px;
        padding: 8px 12px!important;
    }
</style>
